import cx from "classnames"
import { useId } from "react"

import { Label } from "../label"
import { otpInputDefaults } from "./config"
import styles from "./index.module.css"
import { useOtpInput } from "./useOtpInput"

/**
 * Props for the OtpInput component.
 */
export interface OtpInputProps {
  /**
   * The number of digit fields to render.
   */
  length?: number

  /**
   * Callback fired when all digit fields are filled.
   */
  onComplete?: (code: string) => void

  /**
   * Callback fired whenever the OTP value changes.
   */
  onChange?: (code: string) => void

  /**
   * Error message displayed below the input group.
   */
  errorText?: string

  /**
   * Label text displayed above the input group.
   */
  label?: string

  /**
   * Whether to focus the first input field on mount.
   */
  autoFocus?: boolean

  /**
   * Whether the input group is disabled.
   */
  isDisabled?: boolean

  /**
   * Custom HTML id for the first input. Auto-generated if not provided.
   */
  id?: string

  /**
   * Name attribute for a hidden input that holds the joined OTP value.
   * Useful for native form submissions.
   */
  name?: string

  /**
   * Whether the field is marked as required.
   */
  required?: boolean

  /**
   * Tooltip text displayed next to the label.
   */
  tooltip?: string
}

/**
 * A one-time password input component with individual digit fields.
 * Supports paste, browser autofill, keyboard navigation, and accessibility.
 * @param {OtpInputProps} props - The props for the OtpInput component.
 * @returns {React.JSX.Element} - A React JSX element representing the OTP input.
 */
export const OtpInput = ({
  length = otpInputDefaults.LENGTH,
  onComplete,
  onChange,
  errorText,
  label,
  autoFocus = false,
  isDisabled,
  id: providedId,
  name,
  required,
  tooltip,
}: OtpInputProps) => {
  const generatedId = useId()
  const id = providedId ?? generatedId

  const { values, inputRefsRef, handleChange, handleKeyDown, handlePaste, handleFocus } = useOtpInput({
    length,
    autoFocus,
    isDisabled,
    onChange,
    onComplete,
  })

  const hasError = Boolean(errorText)
  const inputClassName = cx(styles.input, { [styles.error]: hasError })

  return (
    <div className={styles.container}>
      {label && <Label label={label} id={id} required={required} disabled={isDisabled} tooltip={tooltip} />}
      <div role="group" aria-label={label ?? "OTP input"} className={styles.inputGroup}>
        {values.map((value, index) => (
          <input
            key={`otp-input-${id}-${index}`}
            ref={(el) => {
              inputRefsRef.current[index] = el
            }}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={length}
            value={value}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={isDisabled}
            className={inputClassName}
            aria-label={`Digit ${index + 1} of ${length}`}
            aria-invalid={hasError}
            autoComplete="one-time-code"
            id={index === 0 ? id : undefined}
          />
        ))}
      </div>
      {name && <input type="hidden" name={name} value={values.join("")} className={styles.hiddenInput} />}
      {errorText && <small className={styles.errorText}>{errorText}</small>}
    </div>
  )
}
